import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
 
import { getVendors, createVendor, getApiErrorMessage } from '../../services/vendorService';
import { Vendor } from '../../types/vendor';
 
import AddVendorModal from '../../compoonents/AddVendorModal/AddVendorModal'; 
import PageLoader from '../../compoonents/PageLoader';
import { PageSearchBar } from '../../compoonents/PageToolbar';
 
import { FiEye } from 'react-icons/fi';
import emptyStateIcon from '../../assets/images/search-icon.jpeg';
import prevIcon from '../../assets/images/previcon.png';
import nextIcon from '../../assets/images/nexticon.png';
 
import './VendorsPage.css';
 
const ITEMS_PER_PAGE = 10;
type VendorCreationData = Omit<Vendor, 'id' | 'createdAt' | 'updatedAt'>;

const VendorsPage: React.FC = () => { 
  const navigate = useNavigate();
 
  // State
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Search State
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch Logic
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); 
      setError(null);
      try {
        // Pass searchTerm to backend
        const response = await getVendors(currentPage, ITEMS_PER_PAGE, searchTerm);
        setVendors(response.data || []);
        setTotalItems(response.count || 0);
        setTotalPages(Math.ceil((response.count || 0) / ITEMS_PER_PAGE));
      } catch (err) {
        setError('Failed to fetch vendors. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    // Debounce to prevent flashing
    const timer = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(timer);
  }, [currentPage, refreshTrigger, searchTerm]);
 
  // Client-Side Fallback Filtering
  const visibleVendors = useMemo(() => {
    if (!searchTerm) return vendors;
    const lowerSearch = searchTerm.toLowerCase();
    return vendors.filter(v => 
      v.name.toLowerCase().includes(lowerSearch) || 
      v.email.toLowerCase().includes(lowerSearch) ||
      (v.phone && v.phone.includes(lowerSearch))
    );
  }, [vendors, searchTerm]);

  // Handlers
  const handleAddNewVendor = async (newVendorData: VendorCreationData) => {
    setIsSaving(true);
    try { 
      const response = await createVendor(newVendorData);
      console.log("Vendor created successfully, API response:", response);
      setIsAddModalOpen(false);
      alert(response.message || "Vendor created successfully!");  
      setRefreshTrigger(prev => prev + 1);  
    } catch (err: unknown) {
      alert(`Error: ${getApiErrorMessage(err, 'Failed to create vendor.')}`);
    } finally {
      setIsSaving(false);
    }
  };
 
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(p => p + 1); };
  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage(p => p - 1); };
  const handleViewDetails = (vendorId: string) => navigate(`/vendors/${vendorId}`);
  const handleOpenAddModal = () => setIsAddModalOpen(true);
 
  return (
    <div className="vendors-page">
      <header className="page-header">
        <h3>All Vendors</h3>
        <div className="page-controls">
          <PageSearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by name, email..."
          />
          <button className="create-vendor-btn" onClick={handleOpenAddModal}>Add New Vendor</button>
        </div>
      </header>

      {/* Loading Overlay */}
      {loading && !error && <PageLoader message="Loading vendors..." />}
      
      {/* Error Message */}
      {error && <div className="page-error">{error}</div>}

      {/* Content */}
      {!loading && !error && (
        <>
          {visibleVendors.length === 0 ? (
            <div className="page--empty">
               {searchTerm ? (
                 <div style={{textAlign: 'center', padding: '40px'}}>
                   <h3>No vendors found matching "{searchTerm}"</h3>
                 </div>
               ) : (
                <>
                  <img src={emptyStateIcon} alt="No vendors" />
                  <h3>No vendors have been added yet</h3>
                </>
               )}
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Date Joined</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleVendors.map((vendor) => (
                      <tr key={vendor.id}>
                        <td data-label="Name">{vendor.name}</td>
                        <td data-label="Email">{vendor.email}</td>
                        <td data-label="Phone">{vendor.phone}</td>
                        <td data-label="Date Joined">{new Date(vendor.createdAt).toLocaleDateString()}</td>
                        <td data-label="Status">
                          <span className={`status-badge ${vendor.isActive ? 'status-active' : 'status-inactive'}`}>
                            {vendor.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td data-label="Action">
                          <div className="table-action-buttons">
                            <button
                              type="button"
                              className="table-action-btn"
                              title="View details"
                              aria-label="View details"
                              onClick={() => handleViewDetails(vendor.id)}
                            >
                              <FiEye />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <footer className="page-footer">
                <div className="pagination-info">
                  Showing page {currentPage} of {totalPages} ({totalItems} total vendors)
                </div>
                <div className="pagination-controls">
                  <button onClick={handlePrevPage} disabled={currentPage === 1}><img src={prevIcon} alt="Previous" /></button>
                  <button onClick={handleNextPage} disabled={currentPage >= totalPages}><img src={nextIcon} alt="Next" /></button>
                </div>
              </footer>
            </>
          )}
        </>
      )}

      {isAddModalOpen && (
        <AddVendorModal
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleAddNewVendor}
          isSaving={isSaving}
        />
      )}
    </div>
  );
};

export default VendorsPage;